import {EditorState, type Extension, RangeSetBuilder, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'
import type {Element, Nodes} from 'hast'
import {SKIP, visitParents} from 'unist-util-visit-parents'

import {getDelimiterType} from '../../language-tools/html/extensions/delimiters'
import {hastPropertiesToHtmlAttributes, isTag} from '../../language-tools/html/hast-utils'
import {isChecked} from '../../language-tools/markdown/list-utils'
import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {CharacterReferenceWidget} from '../widgets/character-reference'
import {EmptySpanWidget} from '../widgets/empty-span'
import {HastElementWidget} from '../widgets/hast-element'
import {RenderErrorWidget} from '../widgets/render-error'
import {TaskListCheckboxWidget} from '../widgets/task-list-checkbox'
import {VideoWidget} from '../widgets/video'
import {activeNodesChanged, activeNodesExtension, getActiveNodes} from './active-nodes'
import {isKnownInvalidVideoUrl, videoUrlsExtension} from './invalid-video-urls'
import {getHtmlTree, htmlTreeChanged, markdownParserExtension} from './markdown-parser'
import styles from './style-contents.module.css'

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{node: Nodes; active: boolean}>

const withinActiveTable = (context: NodeContext) => context.some(parent => parent.active && isTag(parent.node, 'table'))

// `root` is always active when the editor is focused since it covers the entire document
const withinActiveBlock = (context: NodeContext, nodeActive: boolean) =>
  nodeActive || context.some(parent => parent.active && parent.node.type === 'element')

/** Check if this element is a link defined with `[]()` syntax. */
const isResourceLink = (element: Element) =>
  element.tagName === 'a' &&
  element.children.some(child => child.type === 'element' && child.properties?.dataDelimiter === 'resource')

// These aren't exported so we have to extract them from the function parameters :(
type DecorationSpec = Parameters<typeof Decoration.mark>[0] | Parameters<typeof Decoration.replace>[0]

/** Tag names to style as blocks. */
const blockElements = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'dl',
  'p',
  'table',
  'hr',
  'div',
  'details',
  'video',
])

/** Check if this node is a setext heading node with only one character in the delimiter line. */
function isSingleCharSetextHeading(element: Element) {
  return element.children.some(
    child =>
      child.type === 'element' &&
      child.tagName === 'span' &&
      child.properties?.dataDelimiter === 'setextHeadingLine' &&
      child.children?.[0]?.type === 'text' &&
      child.children[0].value.trim().length === 1,
  )
}

function getElementMark(
  element: Element,
  active: boolean,
  context: NodeContext,
  state: EditorState,
): DecorationSpec | undefined {
  const baseClassName = clsx(active && styles.activeBlock, blockElements.has(element.tagName) && styles.block)

  switch (element.tagName) {
    // In an active block, links render with only the `resourceDestination` delimiter node highlighted
    case 'a':
      if (withinActiveBlock(context, active) && isResourceLink(element)) return undefined
      break

    // Tables revert to plain text when active (for now, until we build a table editor)
    case 'table':
      return active ? {tagName: 'div', class: baseClassName} : {widget: new HastElementWidget(element, baseClassName)}
    case 'tr':
    case 'th':
    case 'td':
    case 'thead':
    case 'tbody':
      if (withinActiveTable(context)) return undefined
      break

    // Details/summary tags are not supported in contenteditable
    case 'details':
      return {tagName: 'div', class: baseClassName}
    case 'summary':
      return {tagName: 'div', class: styles.summary}

    // Images and void elements must be rendered with widgets since they fully replace their contents rather than wrapping them
    case 'img':
    case 'hr':
      return active ? {tagName: 'div', class: baseClassName} : {widget: new HastElementWidget(element, baseClassName)}
    case 'video':
      // Known invalid URLs are videos that we tried and failed to render. They probably point to non-video files
      return active ||
        isKnownInvalidVideoUrl(state, typeof element.properties?.src === 'string' ? element.properties.src : '')
        ? {tagName: 'div', class: clsx(baseClassName, styles.muted)}
        : {widget: new VideoWidget(element, baseClassName)}

    case 'br':
      return withinActiveBlock(context, active) ? undefined : {widget: new HastElementWidget(element, baseClassName)}

    // Delimiter special cases
    case 'span': {
      const delimiterType = getDelimiterType(element)
      if (!delimiterType) break
      const raw = element.children[0]?.type === 'text' ? element.children[0].value : ''

      // character reference delimiters have to be replaced with the reference value via a widget
      if (delimiterType === 'characterReference' && !withinActiveBlock(context, active))
        return {widget: new CharacterReferenceWidget(raw)}

      // Links are wierd because we move the anchor to the destination url when active
      if (delimiterType === 'resourceDestination' && withinActiveBlock(context, active))
        return {tagName: 'a', attributes: {href: raw}}

      // Tasklist checkboxes are rendered as clickable/editable checkboxes when not in an active block
      if (delimiterType === 'taskListCheck' && !withinActiveBlock(context, active)) {
        if (!isPositionedNode(element)) break
        const startOffset = element.position.start.offset
        const endOffset = element.position.end.offset

        const checked = isChecked(raw)
        return {
          widget: new TaskListCheckboxWidget({
            checked,
            from: startOffset,
            to: endOffset,
          }),
        }
      }

      break
    }

    case 'h2':
      // Avoid flicker when typing lists that immediately follow text (https://github.com/github/core-ux/issues/838)
      if (active && isSingleCharSetextHeading(element)) return {tagName: 'div', class: baseClassName}
      break
  }

  const {className: nodeClassName, ...properties} = element.properties
  return {
    tagName: element.tagName,
    class: clsx(baseClassName, nodeClassName),
    attributes: hastPropertiesToHtmlAttributes(properties),
  }
}

function getNodeMark(
  node: Nodes,
  active: boolean,
  context: NodeContext,
  state: EditorState,
): DecorationSpec | undefined {
  switch (node.type) {
    case 'comment':
      return {
        tagName: 'code',
        class: styles.comment,
      }
    case 'element':
      return getElementMark(node, active, context, state)
  }
}

function buildDecorations(state: EditorState): DecorationSet {
  const tree = getHtmlTree(state)
  const activeNodes = getActiveNodes(state)

  const builder = new RangeSetBuilder<Decoration>()

  visitParents(tree, (node, parents) => {
    if (!isPositionedNode(node)) return

    const nodeContext: NodeContext = parents.map(p => ({node: p, active: activeNodes.has(p)}))

    const markSpec = getNodeMark(node, activeNodes.has(node), nodeContext, state)
    if (markSpec) {
      // Use `inclusive: true` so the mark includes its start and end positions.
      // This prevents rendering issues when a replace decoration is applied at a node's start or end
      // and would otherwise conflict with the mark.
      const mark = 'widget' in markSpec ? Decoration.replace(markSpec) : Decoration.mark({...markSpec, inclusive: true})

      // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
      // the second tag around it. However, our tree walker encounters parents first and then children, which causes
      // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
      // because the list and item have the same start and end offsets.
      //
      // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
      // element.
      mark.startSide = parents.length
      builder.add(node.position.start.offset, node.position.end.offset, mark)

      // Skip children if a widget was rendered, since the widget will replace all content at this range; any
      // decorations inside this range would be ignored anyway
      if ('widget' in markSpec) return SKIP
    }
  })

  // Place an empty `span` element at the very end of the document, following any line breaks. The DOM will always
  // suppress a line break at the end of a block container, which would prevent the user from typing a newline at the
  // end of the doc. By placing an invisible span after the last character, we ensure that a trailing line break is
  // never the last thing in the document.
  if (isPositionedNode(tree)) {
    const widgetDecoration = Decoration.widget({
      widget: EmptySpanWidget,
      // `side` determines where the cursor ends up relative to this element. Positive ensures the cursor appears before
      // the element, ensuring that it remains 'attached' to text. This also determines order if two decorations have
      // the same position, so we use the max value to ensure this always renders at the very end of the doc.
      side: 10_000,
    })

    builder.add(tree.position.end.offset, tree.position.end.offset, widgetDecoration)
  }

  return builder.finish()
}

let lastReportedError: Error | undefined = undefined

/**
 * Attempt to build decorations, falling back to showing an error widget on failure. Careful error handling here is
 * important because there are many subtle ways this can fail if the code is imperfect (eg, decorations added out of
 * order or with ranges outside the document bounds). The default handling for errors in CodeMirror breaks the editor
 * in unexpected and potentially subtle ways, so this ensures that users know there is an issue and provides a valid
 * fallback.
 */
function tryWithFallback(fn: () => DecorationSet): DecorationSet {
  try {
    const result = fn()
    return result
  } catch (error) {
    if (error instanceof Error) {
      // Errors will repeat on every keystroke if the user keeps typing, which can cause excessive noise in error logs.
      // There's no need to rethrow the same error over and over when nothing has changed.
      if (!lastReportedError || lastReportedError.name !== error.name || lastReportedError.message !== error.message) {
        setTimeout(() => {
          throw error // rethrow async for console logging and error reporting
        })
      }
      lastReportedError = error
    }

    const builder = new RangeSetBuilder<Decoration>()
    builder.add(0, 0, Decoration.widget({widget: new RenderErrorWidget()}))
    return builder.finish()
  }
}

const decorationsState = StateField.define<DecorationSet>({
  create: state => tryWithFallback(() => buildDecorations(state)),
  update: (prev, tr) => {
    const treeChanged = htmlTreeChanged(tr)

    return tryWithFallback(() => {
      if (tr.docChanged && !treeChanged) {
        // 'mapping' the decorations just updates their positions based on where text was inserted/deleted to keep them
        // relatively in sync position-wise. It prevents text from flowing out of the end of nodes, but doesn't
        // actually reflect any Markdown formatting changes. For that, we wait until the tree parse event which we know
        // will quickly follow a docChanged event.
        return prev.map(tr.changes)
      } else if (treeChanged || activeNodesChanged(tr)) {
        return buildDecorations(tr.state)
      } else {
        return prev
      }
    })
  },
  provide: field => [
    markdownParserExtension,
    activeNodesExtension,
    EditorView.outerDecorations.from(field),
    videoUrlsExtension,
  ],
})

export const styleContentsExtension: Extension = decorationsState
